import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import { useDebouncedCallback } from "@react-hookz/web";
import { $getRoot, $getSelection, $isParagraphNode, $isRangeSelection, $isTextNode } from "lexical";
import { uniqBy } from "lodash-es";
import { useEffect, useState } from "react";
import { notEmpty } from "../../../../../utils/not-empty";
import { $isMergeTagV2Node } from "../../nodes/merge-tag-node-v2";

import type { Selection } from "./use-co-pilot-types";
import type { LexicalNode } from "lexical";

export const useLexicalSelection = (): Selection | undefined => {
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<Selection>();

  const updateSelected = useDebouncedCallback(
    () => {
      editor.getEditorState().read(() => {
        if (editor.isComposing()) {
          return;
        }
        const editorSelection = $getSelection();
        const nativeSelection = globalThis.getSelection();
        const rootElement = editor.getRootElement();

        if (
          nativeSelection !== null &&
          (!$isRangeSelection(editorSelection) ||
            rootElement === null ||
            !rootElement.contains(nativeSelection.anchorNode))
        ) {
          return;
        }

        if (!$isRangeSelection(editorSelection)) {
          return;
        }

        const content = editorSelection.getTextContent();
        if (content.length < 1) {
          setSelection(undefined);
          return;
        }

        // Get full content of the selected nodes
        const nodes = editorSelection.getNodes();

        const section = uniqBy(
          nodes
            .map((node) => {
              if ($isParagraphNode(node)) return node;
              else if ($isTextNode(node)) return node.getParent();
            })
            .filter(notEmpty),
          (x) => x.getKey(),
        )
          .map((x) =>
            x
              .getChildren()
              .reduce(
                (text: string, node: LexicalNode) =>
                  text +
                  ($isMergeTagV2Node(node)
                    ? `{{${node.getTextContent()}}}`
                    : node.getTextContent()),
                "",
              ),
          )
          .join("\n");

        setSelection({
          section,
          full: $getRoot().getTextContent(),
        });
      });
    },
    [editor],
    100,
  );

  useEffect(() => {
    document.addEventListener("selectionchange", updateSelected);
    return () => {
      document.removeEventListener("selectionchange", updateSelected);
    };
  }, [updateSelected]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(() => {
          updateSelected();
        }),
        editor.registerRootListener(() => {
          if (editor.getRootElement() === null) setSelection(undefined);
        }),
      ),
    [editor, updateSelected],
  );

  return selection;
};
